import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "antd";
import { currencyformatter } from "../../../Pagat/PaySub/employeeCalc/methods";
import { getReportTypes } from "../types";
import { useLocation } from "react-router";
import { useReportsData } from "../reportContext/reportHelpers";
import dayjsTZ from "../../../../utils/dayjs";
import { htmlParser } from "../../../../utils";
import { useSelector } from "react-redux";

const isHtml = (v) => typeof v === "string" && /<\/?[a-z][\s\S]*>/i.test(v);

const stripHtml = (s) =>
  s
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// reduce any cell value to a primitive safe for Excel/CSV serialization.
// note columns hold React elements (built via htmlParser in the processors);
// passing those to XLSX.json_to_sheet produces a corrupted workbook, so the
// export/print copy must be plain text while the on-screen table keeps the
// rich rendering.
const toPlainCell = (node) => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "number") return node;
  if (typeof node === "string") return isHtml(node) ? stripHtml(node) : node;
  if (Array.isArray(node)) return node.map(toPlainCell).join("");
  if (node?.props?.children !== undefined)
    return toPlainCell(node.props.children);
  return "";
};

// parse a displayed currency/number string ("1,234.50 ALL", "-50") into a number.
const toNumber = (v) => {
  if (typeof v !== "string") return Number(v) || 0;
  const negative = v.trim().startsWith("-");
  const n = Number(
    v
      .replaceAll("ALL", "")
      .replaceAll(" ", "")
      .replaceAll(",", "")
      .replace("-", ""),
  );
  if (Number.isNaN(n)) return 0;
  return negative ? -n : n;
};

function ReportTable({ columnsDef, rowData }) {
  const { state } = useLocation();
  const { isDarkMode } = useSelector((s) => s.usersList);
  const { populateData } = useReportsData();

  const columns = useMemo(() => columnsDef || [], [columnsDef]);

  // column types resolved once per column, not per cell.
  const columnTypes = useMemo(
    () => columns.map((label) => getReportTypes(label)),
    [columns],
  );

  // rows as objects keyed by column label — the shape export/print consume.
  const rows = useMemo(() => {
    if (!rowData) return [];
    return rowData.map((arr) => {
      const obj = {};
      columns.forEach((label, i) => {
        obj[label] = arr[i] ?? "";
      });
      return obj;
    });
  }, [rowData, columns]);

  const [sort, setSort] = useState(() =>
    state === "approvals" ? { key: "Emri", dir: "asc" } : null,
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    const isDate = getReportTypes(key) === "date";
    const factor = dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av = a[key];
      let bv = b[key];
      if (isDate) {
        av = dayjsTZ(av, "DD/MM/YYYY").valueOf();
        bv = dayjsTZ(bv, "DD/MM/YYYY").valueOf();
      }
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * factor;
    });
  }, [rows, sort]);

  // plain-text copy of the rows for export/print — cells flattened to primitives
  // so XLSX/CSV serialization can't choke on React elements (note columns).
  const exportRows = useMemo(
    () =>
      sortedRows.map((row) => {
        const out = {};
        for (const key in row) out[key] = toPlainCell(row[key]);
        return out;
      }),
    [sortedRows],
  );

  // feed the current (sorted) rows to the shared context for export/print.
  useEffect(() => {
    populateData(exportRows);
  }, [exportRows]);

  // totals row: only built when at least one numeric/currency column exists.
  const footer = useMemo(() => {
    const hasNumeric = columnTypes.some(
      (t) => t === "currency" || t === "number",
    );
    if (!hasNumeric || !sortedRows.length) return null;
    return columns.map((label, i) => {
      const type = columnTypes[i];
      if (type !== "currency" && type !== "number") return null;
      const total = sortedRows.reduce(
        (acc, row) => acc + toNumber(row[label]),
        0,
      );
      return type === "currency"
        ? currencyformatter.format(total.toFixed(2))
        : total.toFixed(2);
    });
  }, [sortedRows, columns, columnTypes]);

  const onSort = (key) =>
    setSort((prev) =>
      prev && prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );

  // no rows → render nothing; ReportContent shows the contextual empty-state Alert.
  if (!rowData || rows.length === 0) return null;

  return (
    <>
      <br />
      <div className={`payStub-table2 ${isDarkMode ? "dark" : ""}`}>
        <table>
          <thead>
            <tr>
              {columns.map((label, i) => (
                <th
                  key={`${label}-${i}`}
                  style={{
                    textAlign:
                      columnTypes[i] === "currency" ? "right" : "center",
                    cursor: "pointer",
                  }}
                  onClick={() => onSort(label)}
                >
                  <Tooltip
                    title={
                      sort && sort.key === label && sort.dir === "asc"
                        ? "Sort Descending"
                        : "Sort Ascending"
                    }
                  >
                    {label}
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, ri) => {
              const isHighlighted = row?.["Kategoria e turnit"] === "MUNGESE";
              return (
                <tr
                  key={ri}
                  style={{
                    background: isHighlighted ? "#FE4C4A" : null,
                    color: isHighlighted ? "#ffffff" : null,
                  }}
                >
                  {columns.map((label, ci) => {
                    const type = columnTypes[ci];
                    const rd = row[label];
                    let display;
                    if (type === "currency") {
                      display =
                        typeof rd === "string"
                          ? rd?.replace("ALL", "")
                          : Number(rd).toFixed(2);
                    } else if (isHtml(rd)) {
                      display = htmlParser(rd);
                    } else {
                      display = rd;
                    }
                    return (
                      <td
                        key={`${label}-${ci}`}
                        style={{
                          textAlign: type === "currency" ? "right" : "center",
                        }}
                      >
                        {display}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {footer ? (
            <tfoot>
              <tr>
                {footer.map((val, i) => (
                  <td
                    key={i}
                    style={{
                      background: "#1d3943",
                      color: "#ffffff",
                      border: "1px solid white",
                      textAlign: "right",
                    }}
                  >
                    {i === 0 && val == null ? "Totali" : val}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </>
  );
}

export default ReportTable;
